import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import InputGroup from 'react-bootstrap/InputGroup'
import ListGroup from 'react-bootstrap/ListGroup'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import { createShareLink, listShareLinks, revokeShareLink } from '../../../../types'
import { actionApiCall, formSubmit } from '../../api/actionApiCall'
import { SubmitButton, TextField, useFormState } from '../../components/form'
import { formatDateTime } from '../../utils/format'

export type ShareLinksModalProps = {
    show: boolean
    groupId: string
    onClose: () => void
}

/** Správa odkazů na veřejný náhled skupiny. */
export function ShareLinksModal({ show, groupId, onClose }: ShareLinksModalProps) {
    const queryClient = useQueryClient()
    // Token se vrací jen při vytvoření, proto se drží zvlášť v paměti.
    const [novyOdkaz, setNovyOdkaz] = useState<string | undefined>()

    const { data: odkazy } = useQuery({
        queryKey: ['share-links', groupId],
        queryFn: () => actionApiCall(() => listShareLinks({ path: { groupId } })),
        enabled: show,
    })

    const reload = () => queryClient.invalidateQueries({ queryKey: ['share-links', groupId] })

    const form = useFormState({
        initialValues: { label: '', expiresInDays: '' },
        onSubmit: async (values) => {
            const dny = values.expiresInDays.trim() === '' ? null : Number(values.expiresInDays)
            if (dny !== null && (!Number.isInteger(dny) || dny < 1)) {
                return { fieldErrors: { expiresInDays: 'Zadejte počet dní, nebo pole nechte prázdné.' } }
            }

            const { data, fieldErrors } = await formSubmit(
                () => createShareLink({ path: { groupId }, body: { label: values.label || null, expiresInDays: dny } }),
                { successMessage: 'Odkaz byl vytvořen.' },
            )

            if (!data) {
                return { fieldErrors }
            }

            setNovyOdkaz(`${window.location.origin}/verejne/${data.token}`)
            form.reset()
            await reload()
        },
    })

    const zrus = async (shareLinkId: string) => {
        if (!window.confirm('Opravdu zrušit odkaz? Komukoli, kdo ho má, přestane fungovat.')) {
            return
        }
        const result = await actionApiCall(() => revokeShareLink({ path: { groupId, shareLinkId } }), { successMessage: 'Odkaz byl zrušen.' })
        if (result !== undefined) {
            await reload()
        }
    }

    return (
        <Modal show={show} onHide={onClose} centered scrollable size="lg">
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">
                    Veřejné sdílení skupiny
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className="text-secondary small">
                    Kdo bude mít odkaz, uvidí historii pohybů, členy a nevyrovnané dluhy - bez přihlášení a jen ke čtení.
                    Fotografie účtenek se ve veřejném náhledu nezobrazují.
                </p>

                {novyOdkaz && (
                    <Alert variant="success">
                        <div className="fw-semibold mb-2">Odkaz vytvořen - zkopírujte si ho, znovu se už nezobrazí.</div>
                        <InputGroup>
                            <Form.Control readOnly value={novyOdkaz} aria-label="Vytvořený odkaz" onFocus={(event) => event.target.select()} />
                            <Button variant="outline-secondary" onClick={() => void navigator.clipboard?.writeText(novyOdkaz)}>
                                Kopírovat
                            </Button>
                        </InputGroup>
                    </Alert>
                )}

                <ListGroup className="mb-3">
                    {(odkazy ?? []).map((odkaz) => (
                        <ListGroup.Item key={odkaz.id} className="d-flex align-items-center gap-2">
                            <div className="flex-grow-1 min-w-0">
                                <div className="fw-semibold text-truncate">{odkaz.label || 'Bez popisu'}</div>
                                <div className="small text-secondary">
                                    vytvořen {formatDateTime(odkaz.createdAt)}
                                    {odkaz.expiresAt && ` · platí do ${formatDateTime(odkaz.expiresAt)}`}
                                    {odkaz.lastAccessAt && ` · naposledy otevřen ${formatDateTime(odkaz.lastAccessAt)}`}
                                </div>
                            </div>
                            {odkaz.revoked ? (
                                <Badge bg="secondary">Zrušen</Badge>
                            ) : (
                                <Button variant="outline-danger" size="sm" className="su-tappable" onClick={() => void zrus(odkaz.id)}>
                                    Zrušit
                                </Button>
                            )}
                        </ListGroup.Item>
                    ))}
                    {(odkazy ?? []).length === 0 && <ListGroup.Item className="text-secondary">Zatím tu není žádný odkaz.</ListGroup.Item>}
                </ListGroup>

                <form onSubmit={form.handleSubmit} noValidate>
                    <div className="d-flex gap-2 align-items-start flex-wrap">
                        <div className="flex-grow-1">
                            <TextField
                                label="Popis (nepovinné)"
                                value={form.values.label}
                                onChange={(value) => form.setValue('label', value)}
                                error={form.fieldErrors.label}
                                placeholder="Např. pro rodiče"
                            />
                        </div>
                        <div style={{ maxWidth: '10rem' }}>
                            <TextField
                                label="Platnost ve dnech"
                                type="number"
                                value={form.values.expiresInDays}
                                onChange={(value) => form.setValue('expiresInDays', value)}
                                error={form.fieldErrors.expiresInDays}
                                placeholder="neomezeně"
                                min={1}
                            />
                        </div>
                        <SubmitButton submitting={form.submitting} className="mt-4">
                            Vytvořit odkaz
                        </SubmitButton>
                    </div>
                </form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} className="su-tappable">
                    Zavřít
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
